import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { Input } from '@neo/primitives/input'
import { PopoverClose, PopoverContent } from '@neo/primitives/popover'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { isEmpty } from 'lodash'
import { Search, X } from 'lucide-react'
import { useState } from 'react'

interface Variable {
  key: string
  label: string
  description?: string
}

interface VariableGroup {
  name: string
  id: string
  variables: Variable[]
}

interface BaseVariableListProps {
  title: string
  onClose: () => void
  emptyStateTitle?: string
  emptyStateDescription?: string
  isLoading?: boolean
  error?: string
  sideOffset?: number
  searchTerm?: string
  onSearchChange?: (value: string) => void
}

interface SimpleVariableListProps extends BaseVariableListProps {
  mode: 'simple'
  groups: VariableGroup[]
  onSelect: (variable: string) => void
  groupIcon?: (groupId: string) => React.ReactNode
}

interface AdvancedVariableListProps extends BaseVariableListProps {
  mode: 'advanced'
  groups: VariableGroup[]
  onVariableClick: (variable: Variable) => void
  getGroupIcon?: (groupName: string) => React.ReactNode
}

type VariableListPopoverProps = SimpleVariableListProps | AdvancedVariableListProps

export const VariableListPopover = (props: VariableListPopoverProps) => {
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const {
    mode,
    title,
    onClose,
    emptyStateTitle = 'No variables found',
    emptyStateDescription = '',
    isLoading,
    error,
    sideOffset = 8,
    searchTerm,
    onSearchChange,
    groups,
  } = props

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value)
    } else {
      setLocalSearchTerm(value)
    }
  }

  const currentSearchTerm = searchTerm ?? localSearchTerm

  const filteredGroups = currentSearchTerm
    ? groups
        .map((group) => ({
          ...group,
          variables: group.variables.filter(
            (variable) =>
              variable.label.toLowerCase().includes(currentSearchTerm.toLowerCase()) ||
              variable.description?.toLowerCase().includes(currentSearchTerm.toLowerCase()),
          ),
        }))
        .filter((group) => !isEmpty(group.variables))
    : groups

  const handleVariableClick = (variable: Variable) => {
    if (mode === 'simple') {
      ;(props as SimpleVariableListProps).onSelect(variable.key)
    } else {
      ;(props as AdvancedVariableListProps).onVariableClick(variable)
    }
    onClose()
  }

  const renderGroupIcon = (groupName: string, groupId: string) => {
    if (mode === 'simple') {
      const { groupIcon } = props as SimpleVariableListProps
      return groupIcon?.(groupId)
    }
    if (mode === 'advanced') {
      const { getGroupIcon } = props as AdvancedVariableListProps
      return getGroupIcon?.(groupName)
    }
    return null
  }
  return (
    <PopoverContent
      className="max-h-[min(450px,var(--radix-popover-content-available-height))] max-w-[calc(100vw-2rem)] flex p-4 w-[350px] font-inter"
      side="bottom"
      align="end"
      sideOffset={Math.min(sideOffset, 12)}
      collisionPadding={16}
      onOpenAutoFocus={(e) => {
        // Prevent the popover from stealing focus when it opens
        // This ensures the input field retains focus for continued typing/backspace
        e.preventDefault()
      }}
    >
      <div className="flex min-h-0 flex-col gap-3 w-full">
        <div className="relative flex items-center justify-between">
          <div className="text-h4-500">{title}</div>
          <PopoverClose
            aria-label="Close variables"
            data-testid="button-close-variables"
            className="rounded text-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X size={16} />
          </PopoverClose>
        </div>
        <div className="relative">
          <Input
            placeholder="Search variables..."
            startIcon={Search}
            value={currentSearchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            data-testid="input-search-variables"
          />
        </div>
        <ScrollArea
          className="-mr-4 min-h-0 pr-4"
          viewportClassName="max-h-[min(20rem,calc(var(--radix-popover-content-available-height,28rem)-8rem))]"
        >
          {isLoading && (
            <div className="text-p-400 text-muted-foreground">Loading variables...</div>
          )}
          {error && <div className="text-p-400 text-status-danger">{error}</div>}
          {!isLoading && !error && isEmpty(filteredGroups) && (
            <div className="text-p-400 text-muted-foreground">
              <div className="text-secondary text-h4-500 mb-1">{emptyStateTitle}</div>
              {emptyStateDescription}
            </div>
          )}
          <Accordion
            type="multiple"
            className="space-y-2"
            defaultValue={filteredGroups.length ? [filteredGroups[0].name] : []}
          >
            {filteredGroups.map((group) => (
              <AccordionItem key={group.name} value={group.name} className="border-none">
                <AccordionTrigger className="py-2 hover:no-underline flex justify-start gap-2 group w-full min-w-0">
                  <div className="flex items-center justify-start w-full gap-2  min-w-0">
                    {renderGroupIcon(group.name, group.id)}
                    <h3
                      className="truncate font-inter text-s-500 text-left w-full"
                      title={group.name}
                    >
                      {group.name}
                    </h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-1">
                  <div className="space-y-2">
                    {group.variables.map((variable) => (
                      <button
                        key={variable.key}
                        type="button"
                        onClick={() => handleVariableClick(variable)}
                        className="w-full text-left space-y-0.5 hover:bg-hv1 p-1 px-2 cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid={`button-variable-${variable.key.toLowerCase()}`}
                      >
                        <div className="flex flex-col gap-1 truncate min-w-0">
                          <div className="font-jetbrains text-xs-400 truncate">
                            {variable.label}
                          </div>
                          {variable.description && (
                            <div
                              className="text-s-400 text-tertiary truncate"
                              title={variable.description}
                            >
                              {variable.description}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollArea>
      </div>
    </PopoverContent>
  )
}
